import {
    Layers,
    Scissors,
    Minimize2,
    RotateCw,
    ArrowUpDown,
    Trash2,
    FileImage,
    Images,
    Lock,
    LockOpen,
    PenLine,
    ScanText,
    Archive,
    Sparkles,
    MessageSquareText,
} from "lucide-react";

export const CATEGORIES = [
    { id: "organizar", name: "Organizar" },
    { id: "otimizar", name: "Otimizar" },
    { id: "editar", name: "Editar" },
    { id: "converter", name: "Converter" },
    { id: "seguranca", name: "Segurança" },
    { id: "ia", name: "IA" },
];

export const LIMITS = {
    maxFileMB: 25,
    maxFiles: 10,
    maxPages: 200,
};

export const TOOLS = [
    {
        slug: "juntar",
        name: "Juntar PDFs",
        desc: "Mescle múltiplos PDFs em um único arquivo ordenado.",
        longDesc:
            "Envie dois ou mais arquivos PDF e combine-os em um único documento, na ordem em que foram enviados. Ideal para reunir contratos, relatórios ou apostilas.",
        icon: Layers,
        category: "organizar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: true,
        minFiles: 2,
        options: [],
        keywords: ["juntar", "mesclar", "unir", "combinar", "merge"],
    },
    {
        slug: "dividir",
        name: "Dividir PDF",
        desc: "Separe páginas individuais ou intervalos específicos.",
        longDesc:
            "Extraia partes de um PDF: escolha intervalos de páginas (ex.: 1-3; 4-6) ou separe cada página em um arquivo próprio. Vários resultados chegam em um ZIP.",
        icon: Scissors,
        category: "organizar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "mode",
                label: "Modo de divisão",
                type: "select",
                default: "ranges",
                choices: [
                    { value: "ranges", label: "Por intervalos de páginas" },
                    { value: "all", label: "Cada página em um arquivo" },
                ],
            },
            {
                key: "ranges",
                label: "Intervalos (separados por ponto e vírgula)",
                type: "text",
                placeholder: "ex.: 1-3; 4-6; 8",
                showIf: { key: "mode", value: "ranges" },
            },
        ],
        keywords: ["dividir", "separar", "extrair", "split"],
    },
    {
        slug: "comprimir",
        name: "Comprimir PDF",
        desc: "Diminua o peso mantendo a clareza máxima do texto.",
        longDesc:
            "Reduza o tamanho do seu PDF para enviar por e-mail ou WhatsApp. Escolha o nível: leve (máxima qualidade), médio (equilíbrio) ou forte (menor arquivo).",
        icon: Minimize2,
        category: "otimizar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "level",
                label: "Nível de compressão",
                type: "select",
                default: "medio",
                choices: [
                    { value: "leve", label: "Leve — máxima qualidade" },
                    { value: "medio", label: "Médio — equilíbrio" },
                    { value: "forte", label: "Forte — menor tamanho" },
                ],
            },
        ],
        keywords: ["comprimir", "reduzir", "diminuir", "tamanho", "compress"],
    },
    {
        slug: "girar",
        name: "Girar Páginas",
        desc: "Ajuste orientação para retrato ou paisagem.",
        longDesc:
            "Corrija páginas digitalizadas de lado ou de cabeça para baixo. Gire 90, 180 ou 270 graus, em todas as páginas ou apenas nas que você indicar.",
        icon: RotateCw,
        category: "editar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "degrees",
                label: "Ângulo",
                type: "select",
                default: "90",
                choices: [
                    { value: "90", label: "90° (sentido horário)" },
                    { value: "180", label: "180° (inverter)" },
                    { value: "270", label: "270° (sentido anti-horário)" },
                ],
            },
            {
                key: "pages",
                label: "Páginas (vazio = todas)",
                type: "text",
                placeholder: "ex.: 1-3, 5 — deixe vazio para todas",
                emptyMeans: "todas",
            },
        ],
        keywords: ["girar", "rotacionar", "orientação", "rotate"],
    },
    {
        slug: "reordenar",
        name: "Reordenar Páginas",
        desc: "Defina a sequência correta das páginas.",
        longDesc:
            "Informe a nova ordem das páginas (ex.: 3, 1, 2) e receba o PDF reorganizado. A ordem precisa conter todas as páginas do documento.",
        icon: ArrowUpDown,
        category: "organizar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "order",
                label: "Nova ordem das páginas",
                type: "text",
                placeholder: "ex.: 3, 1, 2, 4",
            },
        ],
        keywords: ["reordenar", "ordenar", "sequência", "reorder"],
    },
    {
        slug: "remover-paginas",
        name: "Remover Páginas",
        desc: "Exclua páginas em branco ou desnecessárias.",
        longDesc:
            "Remova páginas específicas do PDF (ex.: 2, 5-7) e baixe o documento enxuto. Não é possível remover todas as páginas.",
        icon: Trash2,
        category: "editar",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "pages",
                label: "Páginas a remover",
                type: "text",
                placeholder: "ex.: 2, 5-7",
            },
        ],
        keywords: ["remover", "excluir", "apagar", "deletar", "delete"],
    },
    {
        slug: "pdf-para-imagens",
        name: "PDF para Imagens",
        desc: "Exporte páginas para JPG ou PNG de alta resolução.",
        longDesc:
            "Transforme cada página do PDF em uma imagem PNG ou JPG, com resolução de 72, 150 ou 300 DPI. O resultado chega em um arquivo ZIP.",
        icon: FileImage,
        category: "converter",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "format",
                label: "Formato da imagem",
                type: "select",
                default: "png",
                choices: [
                    { value: "png", label: "PNG — sem perda" },
                    { value: "jpg", label: "JPG — menor tamanho" },
                ],
            },
            {
                key: "dpi",
                label: "Resolução",
                type: "select",
                default: "150",
                choices: [
                    { value: "72", label: "72 DPI — tela" },
                    { value: "150", label: "150 DPI — equilíbrio" },
                    { value: "300", label: "300 DPI — impressão" },
                ],
            },
        ],
        keywords: ["pdf para jpg", "pdf para png", "imagem", "converter", "exportar"],
    },
    {
        slug: "imagens-para-pdf",
        name: "Imagens para PDF",
        desc: "Converta fotos e digitalizações em PDF.",
        longDesc:
            "Junte fotos, digitalizações e capturas de tela (JPG, PNG, GIF, WebP, BMP) em um único PDF, uma imagem por página, na ordem enviada.",
        icon: Images,
        category: "converter",
        accept: "image/*",
        acceptLabel: "JPG, PNG, GIF, WebP ou BMP",
        multi: true,
        minFiles: 1,
        options: [],
        keywords: ["jpg para pdf", "png para pdf", "foto", "imagem para pdf", "converter"],
    },
    {
        slug: "pdfa",
        name: "PDF para PDF/A",
        desc: "Converta para o formato de arquivamento com validação real.",
        longDesc:
            "Converta seu PDF para PDF/A (formato de arquivamento de longo prazo) com validação de conformidade veraPDF. Se o documento não atingir o perfil escolhido, você é avisado — nada é entregue como PDF/A sem passar na validação. Atenção: a conversão invalida assinaturas digitais e pode remover formulários interativos e JavaScript.",
        icon: Archive,
        category: "converter",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "profile",
                label: "Perfil PDF/A",
                type: "select",
                default: "2b",
                choices: [
                    { value: "1b", label: "PDF/A-1b — básico (2005)" },
                    { value: "2b", label: "PDF/A-2b — recomendado" },
                    { value: "3b", label: "PDF/A-3b — permite anexos" },
                ],
            },
        ],
        keywords: ["pdf/a", "pdfa", "arquivar", "arquivamento", "arquivo morto", "conformidade"],
    },
    {
        slug: "proteger",
        name: "Proteger PDF",
        desc: "Criptografe o documento com senha (AES-256).",
        longDesc:
            "Adicione uma senha ao seu PDF com criptografia AES-256. Quem abrir o arquivo precisará da senha. Guarde-a com segurança: ela não fica salva em lugar nenhum.",
        icon: Lock,
        category: "seguranca",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            { key: "password", label: "Senha de abertura", type: "password", placeholder: "mínimo 4 caracteres" },
        ],
        keywords: ["proteger", "senha", "criptografar", "bloquear", "segurança"],
    },
    {
        slug: "desproteger",
        name: "Desproteger PDF",
        desc: "Remova a senha de um PDF que é seu.",
        longDesc:
            "Remova a proteção por senha de um PDF informando a senha atual. Use apenas em documentos seus ou que você tem permissão para desbloquear.",
        icon: LockOpen,
        category: "seguranca",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF (pode ter senha)",
        multi: false,
        options: [
            { key: "password", label: "Senha atual do documento", type: "password", placeholder: "senha atual" },
        ],
        keywords: ["desproteger", "remover senha", "desbloquear", "destravar"],
    },
    {
        slug: "assinar",
        name: "Assinar PDF",
        desc: "Desenhe sua assinatura e aplique no documento.",
        longDesc:
            "Desenhe sua assinatura com o mouse ou o dedo e aplique-a na página e posição que escolher. É uma assinatura visual (imagem sobre o PDF) — não substitui certificado digital ICP-Brasil.",
        icon: PenLine,
        category: "seguranca",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        signature: true,
        options: [
            {
                key: "pages",
                label: "Onde assinar",
                type: "select",
                default: "ultima",
                choices: [
                    { value: "ultima", label: "Última página" },
                    { value: "todas", label: "Todas as páginas" },
                    { value: "personalizada", label: "Páginas específicas" },
                ],
            },
            {
                key: "pages_custom",
                label: "Páginas (ex.: 1, 3-4)",
                type: "text",
                placeholder: "ex.: 1, 3-4",
                showIf: { key: "pages", value: "personalizada" },
            },
            {
                key: "position",
                label: "Posição",
                type: "select",
                default: "inf-dir",
                choices: [
                    { value: "inf-dir", label: "Inferior direito" },
                    { value: "inf-esq", label: "Inferior esquerdo" },
                    { value: "sup-dir", label: "Superior direito" },
                    { value: "sup-esq", label: "Superior esquerdo" },
                ],
            },
            {
                key: "scale",
                label: "Tamanho",
                type: "select",
                default: "media",
                choices: [
                    { value: "pequena", label: "Pequena" },
                    { value: "media", label: "Média" },
                    { value: "grande", label: "Grande" },
                ],
            },
        ],
        keywords: ["assinar", "assinatura", "rubrica", "sign"],
    },
    {
        slug: "ocr",
        name: "OCR — Reconhecer Texto",
        desc: "Transforme digitalizações em PDF pesquisável.",
        longDesc:
            "Aplique reconhecimento óptico de caracteres (OCR) em PDFs digitalizados ou de imagem e torne o texto pesquisável e copiável. Suporta português e inglês. Etapa mais demorada: alguns segundos por página, até 60 páginas.",
        icon: ScanText,
        category: "converter",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        options: [
            {
                key: "language",
                label: "Idioma do documento",
                type: "select",
                default: "por",
                choices: [
                    { value: "por", label: "Português" },
                    { value: "eng", label: "Inglês" },
                    { value: "por+eng", label: "Português + Inglês" },
                ],
            },
        ],
        keywords: ["ocr", "reconhecer texto", "digitalizado", "escaneado", "pesquisável"],
    },
    {
        slug: "resumir-pdf",
        name: "Resumir PDF com IA",
        desc: "Resumo automático com Claude, GPT ou Gemini.",
        longDesc:
            "A IA lê o documento e devolve um resumo com ideia principal, pontos-chave e conclusão. Você escolhe o modelo: Claude Sonnet 5.5, GPT 5.6 Terra ou Gemini 3.1 Pro. O texto vai ao provedor de IA apenas para gerar a resposta e a sessão expira em 60 minutos.",
        icon: Sparkles,
        category: "ia",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        ai: "resumir",
        options: [],
        keywords: ["resumir", "resumo", "ia", "inteligência artificial", "sumário"],
    },
    {
        slug: "perguntar-pdf",
        name: "Perguntar ao PDF",
        desc: "Converse com o documento e tire dúvidas com IA.",
        longDesc:
            "Envie um PDF e faça perguntas em linguagem natural sobre o conteúdo — contratos, apostilas, relatórios. Escolha entre Claude, GPT ou Gemini. A conversa expira em 60 minutos junto com o documento.",
        icon: MessageSquareText,
        category: "ia",
        accept: ".pdf,application/pdf",
        acceptLabel: "PDF",
        multi: false,
        ai: "perguntar",
        options: [],
        keywords: ["perguntar", "chat", "conversar", "ia", "dúvidas", "perguntas"],
    },
];

export const getTool = (slug) => TOOLS.find((t) => t.slug === slug);

export const relatedTools = (tool) =>
    TOOLS.filter((t) => t.category === tool.category && t.slug !== tool.slug).slice(0, 3);
